import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGuestAuth } from '@/lib/contexts/guest-auth-context';

/**
 * Page d'accueil pour les invitations de rapport de procédure
 * Affiche le contexte de l'invitation et gère l'authentification OTP
 */
export default function InviteLandingPage() {
    const { t } = useTranslation('public');
    const navigate = useNavigate();
    const {
        invitationContext,
        isAuthenticated,
        isCheckingSession,
        isLoading,
        error,
        invitationUuid,
        loadInvitationContext,
        requestOtp,
        verifyOtp,
        clearError,
    } = useGuestAuth();

    const [step, setStep] = useState<'loading' | 'welcome' | 'otp'>('loading');
    const [otpCode, setOtpCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const inputRefs = useRef<HTMLInputElement[]>([]);

    // Charger le contexte de l'invitation au montage
    useEffect(() => {
        console.log('[Landing] Mount - isAuthenticated:', isAuthenticated, 'invitationContext:', invitationContext?.status);
        loadInvitationContext().then((context) => {
            console.log('[Landing] Context loaded, status:', context?.status);
            if (context) {
                setStep('welcome');
            } else {
                // Erreur de chargement (403, 404, etc.) → sortir du spinner
                setStep('welcome');
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Si déjà authentifié (session restaurée via cookie), naviguer directement au rapport
    useEffect(() => {
        if (!isCheckingSession && isAuthenticated && invitationContext && invitationContext.status !== 'completed') {
            navigate(`/public/invite/${invitationUuid}/report`, { replace: true });
        }
    }, [isCheckingSession, isAuthenticated, invitationContext, invitationUuid, navigate]);

    // Gestion du countdown pour renvoyer le code
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Si l'OTP n'est pas requis ET le rapport n'est pas déjà finalisé, passer directement au rapport
    // Note: OTP est maintenant toujours obligatoire, ce cas ne devrait plus se produire
    useEffect(() => {
        if (invitationContext && !invitationContext.is_otp_required && invitationContext.status !== 'completed') {
            verifyOtp('000000').then((success) => {
                if (success) {
                    navigate(`/public/invite/${invitationUuid}/report`);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invitationContext]);

    const handleRequestOtp = async () => {
        console.log('[Landing] handleRequestOtp - status:', invitationContext?.status);
        // Ne pas permettre de demander un code si le rapport est déjà finalisé
        if (invitationContext?.status === 'completed') {
            console.log('[Landing] handleRequestOtp BLOCKED - already completed');
            return;
        }
        
        clearError();
        const success = await requestOtp();
        console.log('[Landing] requestOtp result:', success);
        if (success) {
            setOtpSent(true);
            setStep('otp');
            setCountdown(60); // 60 secondes avant de pouvoir renvoyer
        }
    };

    const handleResendOtp = async () => {
        if (countdown > 0) return;
        clearError();
        const success = await requestOtp();
        if (success) {
            setCountdown(60);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) {
            // Si on colle un code complet
            const pastedCode = value.replace(/\D/g, '').slice(0, 6);
            setOtpCode(pastedCode);
            if (pastedCode.length === 6) {
                inputRefs.current[5]?.focus();
            }
            return;
        }

        const newCode = otpCode.split('');
        newCode[index] = value;
        const updatedCode = newCode.join('').slice(0, 6);
        setOtpCode(updatedCode);

        // Focus sur le champ suivant
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyOtp = async () => {
        console.log('[Landing] handleVerifyOtp - status:', invitationContext?.status, 'otpCode:', otpCode);
        // Ne pas permettre de valider si le rapport est déjà finalisé
        if (invitationContext?.status === 'completed') {
            console.log('[Landing] handleVerifyOtp BLOCKED - already completed');
            return;
        }
        
        clearError();
        const success = await verifyOtp(otpCode);
        console.log('[Landing] verifyOtp result:', success, 'navigating to report');
        if (success) {
            navigate(`/public/invite/${invitationUuid}/report`);
        } else {
            // OTP invalide ou expiré → vider le code et débloquer le renvoi
            setOtpCode('');
            setCountdown(0);
            inputRefs.current[0]?.focus();
        }
    };

    // État de chargement initial
    if (step === 'loading') {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-secondary" />
                    <p className="text-[14px] text-muted-foreground">{t('invite.loading')}</p>
                </div>
            </div>
        );
    }

    // Erreur de chargement
    if (!invitationContext) {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center px-[20px] pb-16">
                <div className="max-w-md w-full bg-card rounded-[16px] shadow-lg p-[32px] text-center">
                    <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-destructive" />
                    </div>
                    <h1 className="text-[20px] font-semibold mb-2">{t('invite.notFoundTitle')}</h1>
                    <p className="text-[14px] text-muted-foreground mb-6">{t('invite.notFoundDescription')}</p>
                </div>
            </div>
        );
    }

    // Invitation révoquée ou expirée
    if (invitationContext.status === 'revoked') {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center px-[20px] pb-16">
                <div className="max-w-md w-full bg-card rounded-[16px] shadow-lg p-[32px] text-center">
                    <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-destructive" />
                    </div>
                    <h1 className="text-[20px] font-semibold mb-2">{t('invite.revokedTitle')}</h1>
                    <p className="text-[14px] text-muted-foreground">{t('invite.revokedDescription')}</p>
                </div>
            </div>
        );
    }

    if (invitationContext.status === 'completed') {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center px-[20px] pb-16">
                <div className="max-w-md w-full bg-card rounded-[16px] shadow-lg p-[32px] text-center">
                    <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="h-8 w-8 text-secondary" />
                    </div>
                    <h1 className="text-[20px] font-semibold mb-2">{t('invite.alreadySubmittedTitle')}</h1>
                    <p className="text-[14px] text-muted-foreground">{t('invite.alreadySubmittedDescription')}</p>
                </div>
            </div>
        );
    }

    // Utilisateur déjà authentifié - proposer de reprendre
    if (isAuthenticated && invitationContext) {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center px-[20px] pb-16">
                <div className="max-w-md w-full">
                    <div className="bg-card rounded-[16px] shadow-lg p-[32px]">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="h-8 w-8 text-secondary" />
                            </div>
                            <h1 className="text-[20px] font-semibold mb-2">
                                {t('invite.sessionInProgressTitle')}
                            </h1>
                            <p className="text-[14px] text-muted-foreground">
                                {t('invite.sessionInProgressDescription')}
                                <span className="font-medium text-foreground">
                                    {' '}{invitationContext.procedure_template.name}
                                </span>
                            </p>
                        </div>

                        <Button
                            onClick={() => navigate(`/public/invite/${invitationUuid}/report`)}
                            className="w-full h-auto rounded-[12px] py-[16px] text-[16px] bg-brand text-foreground-dark hover:bg-brand/90 mb-3"
                        >
                            {t('invite.resumeButton')}
                        </Button>

                        <p className="text-[12px] text-muted-foreground text-center">
                            {t('invite.progressSaved')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center px-[20px] pb-16">
            <div className="max-w-md w-full">
                <div className="bg-card rounded-[16px] shadow-lg p-[32px]">
                    {step === 'welcome' && (
                        <>
                            {/* Contexte de l'invitation */}
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Mail className="h-8 w-8 text-secondary" />
                                </div>
                                <h1 className="text-[20px] font-semibold mb-2">{t('invite.welcomeTitle')}</h1>
                                <p className="text-[14px] text-muted-foreground">
                                    {t('invite.welcomeDescription', { companyName: invitationContext.company.name })}
                                    <span className="font-medium text-foreground">
                                        {' '}{invitationContext.procedure_template.name}
                                    </span>
                                </p>
                            </div>

                            {/* Infos */}
                            <div className="bg-muted/50 rounded-[12px] p-[16px] mb-6">
                                <div className="space-y-[12px]">
                                    {invitationContext.folder && (
                                        <div className="flex justify-between">
                                            <span className="text-[13px] text-muted-foreground">{t('invite.folderLabel')}</span>
                                            <span className="text-[13px] font-medium">{invitationContext.folder.name}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-[13px] text-muted-foreground">{t('invite.emailLabel')}</span>
                                        <span className="text-[13px] font-medium">{invitationContext.email}</span>
                                    </div>
                                    {/* [DISABLED] Expiration — décommentez pour réactiver l'affichage de la date d'expiration */}
                                    {/*
                                    <div className="flex justify-between">
                                        <span className="text-[13px] text-muted-foreground">Expire le</span>
                                        <span className="text-[13px] font-medium">
                                            {new Date(invitationContext.expires_at).toLocaleDateString('fr-FR')}
                                        </span>
                                    </div>
                                    */}
                                </div>
                            </div>

                            {error && (
                                <div className="bg-destructive/10 text-destructive rounded-lg p-3 mb-4 text-sm flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {error}
                                </div>
                            )}

                            <Button 
                                onClick={handleRequestOtp} 
                                disabled={isLoading}
                                className="w-full h-auto rounded-[12px] py-[16px] text-[16px] bg-brand text-foreground-dark hover:bg-brand/90"
                            >
                                {isLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : null}
                                {t('invite.requestCodeButton')}
                            </Button>

                            <p className="text-[12px] text-muted-foreground text-center mt-4">
                                {t('invite.codeWillBeSent')}
                            </p>
                        </>
                    )}

                    {step === 'otp' && (
                        <>
                            <div className="text-center mb-8">
                                <h1 className="text-xl font-semibold mb-2">{t('invite.otpTitle')}</h1>
                                <p className="text-muted-foreground text-sm">
                                    {t('invite.otpDescription')}
                                    <span className="block font-medium text-foreground mt-1">
                                        {invitationContext.email}
                                    </span>
                                </p>
                            </div>

                            {/* Champs OTP */}
                            <div className="flex justify-center gap-2 mb-6">
                                {[0, 1, 2, 3, 4, 5].map((index) => (
                                    <Input
                                        key={index}
                                        ref={(el) => { if (el) inputRefs.current[index] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={index === 0 ? 6 : 1}
                                        value={otpCode[index] || ''}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        className="w-12 h-14 text-center text-xl font-semibold"
                                    />
                                ))}
                            </div>

                            {error && (
                                <div className="bg-destructive/10 text-destructive rounded-lg p-3 mb-4 text-sm flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {error}
                                </div>
                            )}

                            <Button
                                onClick={handleVerifyOtp}
                                disabled={isLoading || otpCode.length !== 6}
                                className="w-full h-auto rounded-[12px] py-[16px] text-[16px] bg-brand text-foreground-dark hover:bg-brand/90 mb-4"
                            >
                                {isLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : null}
                                {t('invite.verifyCodeButton')}
                            </Button>

                            <p className="text-[12px] text-muted-foreground text-center mb-4">
                                {t('invite.codeValidFor')}
                            </p>

                            <div className="text-center">
                                <Button
                                    variant="link"
                                    color="secondary"
                                    onClick={handleResendOtp}
                                    disabled={countdown > 0 || isLoading}
                                    className="text-[14px] disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                                >
                                    {t('invite.resendCodeButton')}
                                </Button>
                            </div>

                            <Button
                                variant="link"
                                color="neutral"
                                onClick={() => setStep('welcome')}
                                className="w-full mt-4 text-[14px]"
                            >
                                {t('invite.backButton')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
